import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Camera, LogOut, Trash2, ChevronRight, Wallet, Sparkles, Users, ShieldCheck, MailCheck, Bell, Upload, Image as ImageIcon, X, ZoomIn, Check, Phone, Crown, CalendarCheck, CalendarDays, GraduationCap } from "lucide-react";
import { api, formatError, avatarSrc, fmtDate, fmtDateTime } from "../lib/api";
import MilestoneTracker from "../components/MilestoneTracker";
import InstallPrompt from "../components/InstallPrompt";

const urlB64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
};
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Screen, PageHeader } from "../components/ui-kit";

const ROLE_LABEL = { guest: "Guest", member: "Member", instructor: "Instructor", admin: "Admin", super_admin: "Super Admin" };

const DanceOptInToggle = () => {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/me/dance-optin").then((r) => setOn(!!r.data.dance_level_optin)).catch(() => {}); }, []);
  const toggle = async () => {
    setBusy(true);
    const next = !on;
    try { await api.put("/me/dance-optin", { enabled: next }); setOn(next); toast.success(next ? "Dance levels enabled on your account" : "Dance levels turned off"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-5 mt-3" data-testid="dance-optin-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <GraduationCap className="text-[#D4AF37] shrink-0" size={22} />
          <div className="min-w-0"><p className="font-medium">Learn mode — dance levels</p><p className="text-[#A19DB0] text-xs">Track your own level progression like a student. Off by default for super admins.</p></div>
        </div>
        <button onClick={toggle} disabled={busy} role="switch" aria-checked={on} data-testid="dance-optin-switch"
          className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${on ? "bg-[#D4AF37]" : "bg-white/15"}`}>
          <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-transform ${on ? "translate-x-5" : ""}`} />
        </button>
      </div>
    </div>
  );
};

export default function Profile() {
  const { user, setUser, checkAuth, logout } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const hl = params.get("complete");
  useEffect(() => {
    if (hl) setTimeout(() => document.getElementById(`hl-${hl}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 400);
  }, [hl]);
  const fileRef = useRef();
  const [name, setName] = useState(user.name);
  const [phone, setPhone] = useState(user.phone || "");
  const [phoneVis, setPhoneVis] = useState(user.phone_visibility || "friends");
  const [phoneSel, setPhoneSel] = useState(user.phone_visible_to || []);
  const [friends, setFriends] = useState([]);
  const [saving, setSaving] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [code, setCode] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushOn, setPushOn] = useState(typeof Notification !== "undefined" && Notification.permission === "granted");
  const [milestones, setMilestones] = useState(null);

  useEffect(() => { api.get("/milestones").then((r) => setMilestones(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/friends").then((r) => setFriends(r.data.friends || [])).catch(() => {}); }, []);
  const isAdmin = ["admin", "super_admin"].includes(user.role);
  const [attendance, setAttendance] = useState([]);
  useEffect(() => { api.get("/attendance").then((r) => setAttendance(r.data)).catch(() => {}); }, []);
  const delAttendance = async (aid) => {
    if (!window.confirm("Delete this attendance record? This cannot be undone.")) return;
    try { await api.delete(`/attendance/${aid}`); setAttendance((a) => a.filter((x) => x.id !== aid)); toast.success("Attendance record deleted"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const [bio, setBio] = useState("");
  const [bioBusy, setBioBusy] = useState(false);
  useEffect(() => { setBio(user?.bio || ""); }, [user?.bio]);
  const saveBio = async () => {
    setBioBusy(true);
    try { await api.put("/profile/bio", { bio }); await checkAuth(); toast.success("Bio saved"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBioBusy(false); }
  };

  const enablePush = async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) { toast.error("Push isn't supported on this device"); return; }
    setPushBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Notifications are blocked in your browser settings"); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-public-key");
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8Array(data.key) });
      await api.post("/push/subscribe", sub.toJSON());
      setPushOn(true); toast.success("Push notifications enabled");
    } catch (e) { toast.error("Could not enable push notifications"); }
    finally { setPushBusy(false); }
  };

  const sendCode = async () => {
    setOtpBusy(true);
    try { await api.post("/auth/resend-otp"); setOtpSent(true); toast.success("Code sent to your email"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setOtpBusy(false); }
  };
  const verifyEmail = async () => {
    if (!code.trim()) { toast.error("Enter the code"); return; }
    setOtpBusy(true);
    try { await api.post("/auth/verify-otp", { email: user.email, code: code.trim() }); await checkAuth(); toast.success("Email verified!"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setOtpBusy(false); }
  };

  const saveProfile = async () => {
    setSaving(true);
    try { const r = await api.put("/profile", { name, phone, phone_visibility: phoneVis, phone_visible_to: phoneSel }); setUser(r.data); toast.success("Profile saved"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const FRAME = 260;
  const camRef = useRef();
  const imgElRef = useRef(null);
  const dragRef = useRef(null);
  const [sheet, setSheet] = useState(false);
  const [editSrc, setEditSrc] = useState(null);
  const [nat, setNat] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [off, setOff] = useState({ x: 0, y: 0 });

  const metrics = () => {
    if (!nat) return null;
    const base = Math.max(FRAME / nat.w, FRAME / nat.h);
    const scale = base * zoom;
    return { scale, dw: nat.w * scale, dh: nat.h * scale };
  };
  const disp = () => { const m = metrics(); return m ? { w: m.dw, h: m.dh } : { w: "auto", h: "auto" }; };
  const clamp = (x, y, m) => ({ x: Math.min(0, Math.max(FRAME - m.dw, x)), y: Math.min(0, Math.max(FRAME - m.dh, y)) });

  const onFilePicked = (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    setSheet(false);
    const r = new FileReader();
    r.onload = () => { setNat(null); setZoom(1); setOff({ x: 0, y: 0 }); setEditSrc(r.result); };
    r.readAsDataURL(f);
  };
  const onImgLoad = () => {
    const img = imgElRef.current; if (!img) return;
    const w = img.naturalWidth, h = img.naturalHeight;
    const base = Math.max(FRAME / w, FRAME / h);
    setNat({ w, h });
    setOff({ x: (FRAME - w * base) / 2, y: (FRAME - h * base) / 2 });
  };
  useEffect(() => {
    const m = metrics();
    if (m) setOff((o) => clamp(o.x, o.y, m));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom]);

  const posFrom = (e) => (e.touches ? e.touches[0] : e);
  const startDrag = (e) => { const p = posFrom(e); dragRef.current = { px: p.clientX, py: p.clientY, ox: off.x, oy: off.y }; };
  const moveDrag = (e) => { if (!dragRef.current) return; const m = metrics(); if (!m) return; const p = posFrom(e); setOff(clamp(dragRef.current.ox + (p.clientX - dragRef.current.px), dragRef.current.oy + (p.clientY - dragRef.current.py), m)); };
  const endDrag = () => { dragRef.current = null; };

  const saveAvatar = async () => {
    const img = imgElRef.current, m = metrics(); if (!img || !m) return;
    const OUT = 400, c = document.createElement("canvas"); c.width = OUT; c.height = OUT;
    const ctx = c.getContext("2d");
    const sSize = FRAME / m.scale, sx = -off.x / m.scale, sy = -off.y / m.scale;
    ctx.drawImage(img, sx, sy, sSize, sSize, 0, 0, OUT, OUT);
    c.toBlob(async (blob) => {
      const fd = new FormData(); fd.append("file", blob, "avatar.png");
      try { await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } }); await checkAuth(); toast.success("Photo updated"); setEditSrc(null); }
      catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    }, "image/png");
  };
  const deleteAvatar = async () => {
    setSheet(false);
    try { await api.delete("/profile/avatar"); await checkAuth(); toast.success("Photo removed"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const del = async () => {
    try { await api.delete("/auth/account"); toast.success("Account deleted"); setUser(false); navigate("/login"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const links = [
    { label: "Wallet & passes", icon: Wallet, to: "/wallet", tid: "profile-wallet" },
    { label: "Buy packages", icon: Sparkles, to: "/packages", tid: "profile-packages" },
    { label: "Friends", icon: Users, to: "/friends", tid: "profile-friends" },
  ];

  return (
    <Screen>
      <PageHeader overline="Account" title="Profile" />
      <div className="flex flex-col items-center">
        <div className="relative">
          <button onClick={() => setSheet(true)} data-testid="avatar-upload-btn" className="block rounded-full">
            <img src={avatarSrc(user)} alt="" className="w-40 h-40 sm:w-44 sm:h-44 rounded-full object-cover border-2 border-[#D4AF37]/40" />
          </button>
          <span className="pointer-events-none absolute bottom-0 right-0 w-8 h-8 rounded-full bg-[#D4AF37] text-black flex items-center justify-center"><Camera size={16} /></span>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFilePicked} data-testid="avatar-input" />
          <input ref={camRef} type="file" accept="image/*" capture="user" hidden onChange={onFilePicked} data-testid="avatar-camera-input" />
        </div>
        <h2 className="font-display text-2xl font-semibold mt-3">{user.name}</h2>
        <p className="text-[#A19DB0] text-sm">{user.email}</p>
        <div className="flex flex-wrap items-center justify-center gap-2 mt-2 text-xs">
          <span className="flex items-center gap-1.5 bg-[#9D4EDD]/20 text-[#c79bff] px-3 py-1 rounded-full"><ShieldCheck size={13} /> {ROLE_LABEL[user.role] || user.role}</span>
          <span data-testid="profile-join-date" className="flex items-center gap-1.5 bg-white/5 text-[#A19DB0] px-3 py-1 rounded-full"><CalendarDays size={13} /> Member since {fmtDate(user.join_date)}</span>
        </div>
      </div>

      {user.role === "vip" && (
        <div data-testid="profile-vip-perks" className="mt-6 rounded-2xl border border-[#EC4899]/40 bg-[#EC4899]/10 p-5">
          <p className="font-display text-lg font-semibold flex items-center gap-2"><Crown size={18} className="text-[#EC4899]" fill="#EC4899" /> VIP member perks</p>
          <ul className="mt-3 space-y-2 text-sm">
            <li className="flex gap-2"><Check size={16} className="text-[#EC4899] mt-0.5 shrink-0" /> Solo social pass at S$13{user.vip_lifetime ? " for life" : ""} — save S$2 every time</li>
            <li className="flex gap-2"><Check size={16} className="text-[#EC4899] mt-0.5 shrink-0" /> Exclusive — VIP pricing shown across the Packages page</li>
            <li className="flex gap-2"><Check size={16} className="text-[#EC4899] mt-0.5 shrink-0" /> Priority recognition — your VIP badge shows to the team</li>
          </ul>
        </div>
      )}

      {user.email_verified === false && (
        <div id="hl-email" data-testid="verify-email-card" className={`mt-6 rounded-2xl border bg-[#D4AF37]/[0.08] p-5 transition-shadow ${hl === "email" ? "border-[#D4AF37] ring-2 ring-[#D4AF37]" : "border-[#D4AF37]/40"}`}>
          <p className="font-display text-lg font-semibold flex items-center gap-2"><MailCheck size={18} className="text-[#D4AF37]" /> Verify your email</p>
          <p className="text-[#A19DB0] text-sm mt-1">Verify <span className="text-[#E8E4F0]">{user.email}</span> to unlock purchases.</p>
          {!otpSent ? (
            <button onClick={sendCode} disabled={otpBusy} data-testid="send-otp" className="mt-3 w-full rounded-full bg-[#D4AF37] text-black font-semibold py-2.5 active:scale-[0.98] transition-transform disabled:opacity-60">{otpBusy ? "Sending…" : "Send verification code"}</button>
          ) : (
            <>
              <div className="flex gap-2 mt-3">
                <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} data-testid="otp-input" inputMode="numeric" placeholder="6-digit code" className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm tracking-widest outline-none focus:border-[#D4AF37] placeholder:text-white/30" />
                <button onClick={verifyEmail} disabled={otpBusy} data-testid="verify-otp" className="rounded-xl bg-[#D4AF37] text-black font-semibold px-5 disabled:opacity-60">Verify</button>
              </div>
              <button onClick={sendCode} disabled={otpBusy} data-testid="resend-otp" className="text-xs text-[#A19DB0] mt-2 underline">Resend code</button>
            </>
          )}
        </div>
      )}

      {sheet && (
        <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-end" onClick={() => setSheet(false)} data-testid="avatar-sheet">
          <div className="w-full max-w-md mx-auto bg-[#1a1424] border-t border-white/10 rounded-t-3xl p-5 pb-[max(env(safe-area-inset-bottom),1.25rem)]" onClick={(e) => e.stopPropagation()}>
            <div className="w-10 h-1 rounded-full bg-white/20 mx-auto mb-5" />
            <button onClick={() => { setSheet(false); camRef.current?.click(); }} data-testid="avatar-take" className="w-full flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-4 mb-2.5"><span className="w-10 h-10 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] flex items-center justify-center"><Camera size={20} /></span><span className="font-medium">Take photo</span></button>
            <button onClick={() => { setSheet(false); fileRef.current?.click(); }} data-testid="avatar-library" className="w-full flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-4 mb-2.5"><span className="w-10 h-10 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] flex items-center justify-center"><ImageIcon size={20} /></span><span className="font-medium">Upload from library</span></button>
            {user.avatar_path && <button onClick={deleteAvatar} data-testid="avatar-delete" className="w-full flex items-center gap-3 rounded-2xl bg-[#E57373]/10 border border-[#E57373]/20 p-4"><span className="w-10 h-10 rounded-full bg-[#E57373]/15 text-[#ff8a8a] flex items-center justify-center"><Trash2 size={20} /></span><span className="font-medium text-[#ff8a8a]">Delete photo</span></button>}
          </div>
        </div>
      )}
      {editSrc && (
        <div className="fixed inset-0 z-[75] bg-black/85 backdrop-blur-sm flex items-center justify-center p-6" data-testid="avatar-editor">
          <div className="w-full max-w-sm rounded-3xl bg-[#1a1424] border border-white/10 p-5">
            <div className="flex items-center justify-between mb-4"><h3 className="font-display text-lg font-semibold">Reposition</h3><button onClick={() => setEditSrc(null)} className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center"><X size={18} /></button></div>
            <div className="mx-auto rounded-full overflow-hidden border-2 border-[#D4AF37]/40 relative touch-none select-none cursor-move" style={{ width: FRAME, height: FRAME }}
              onMouseDown={startDrag} onMouseMove={moveDrag} onMouseUp={endDrag} onMouseLeave={endDrag}
              onTouchStart={startDrag} onTouchMove={moveDrag} onTouchEnd={endDrag}>
              <img ref={imgElRef} src={editSrc} onLoad={onImgLoad} alt="" draggable={false} className="absolute max-w-none" style={{ left: off.x, top: off.y, width: disp().w, height: disp().h }} />
            </div>
            <div className="flex items-center gap-2 mt-4"><ZoomIn size={16} className="text-[#A19DB0]" /><input type="range" min="1" max="3" step="0.01" value={zoom} onChange={(e) => setZoom(parseFloat(e.target.value))} data-testid="avatar-zoom" className="flex-1 accent-[#D4AF37]" /></div>
            <div className="flex gap-2 mt-4"><button onClick={() => setEditSrc(null)} className="flex-1 rounded-full bg-white/10 border border-white/10 py-2.5 text-sm">Cancel</button><button onClick={saveAvatar} data-testid="avatar-save" className="flex-1 rounded-full bg-[#D4AF37] text-black font-semibold py-2.5 text-sm">Save photo</button></div>
          </div>
        </div>
      )}

      <div className="mt-6"><InstallPrompt /></div>

      {milestones && <div className="mt-6"><MilestoneTracker data={milestones} /></div>}

      {["instructor", "artist"].includes(user.role) && (
        <div data-testid="bio-editor" className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-5">
          <p className="font-display text-lg font-semibold flex items-center gap-2"><Users size={18} className="text-[#D4AF37]" /> Your team bio</p>
          <p className="text-[#A19DB0] text-xs mt-0.5">Shown to members on the "Meet the team" page.</p>
          <textarea value={bio} onChange={(e) => setBio(e.target.value)} data-testid="bio-textarea" rows={4} maxLength={600}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 mt-3 text-sm outline-none focus:border-[#D4AF37] resize-none" placeholder="Tell members about your dance journey…" />
          <button onClick={saveBio} disabled={bioBusy} data-testid="save-bio" className="mt-3 rounded-full bg-[#D4AF37] text-black font-semibold px-5 py-2.5 text-sm active:scale-95 transition-transform disabled:opacity-60">{bioBusy ? "Saving…" : "Save bio"}</button>
        </div>
      )}
      <div className="mt-8 space-y-4">
        {["admin", "super_admin"].includes(user.role) && (
          <button onClick={() => navigate("/admin")} data-testid="profile-admin-link"
            className="w-full flex items-center justify-center gap-2 bg-[#9D4EDD]/20 border border-[#9D4EDD]/40 text-[#c79bff] rounded-full py-3.5 font-semibold active:scale-[0.98] transition-transform">
            <ShieldCheck size={18} /> Open Admin Dashboard
          </button>
        )}
        <div>
          <label className="text-xs text-[#A19DB0]">Display name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] outline-none py-2 mt-1 transition-colors" />
        </div>
        <div>
          <label className="text-xs text-[#A19DB0]">Phone</label>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="profile-phone" placeholder="+65 ..." className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] outline-none py-2 mt-1 placeholder:text-white/30 transition-colors" />
        </div>
        <div>
          <label className="text-xs text-[#A19DB0]">Who can see my phone</label>
          <div className="grid grid-cols-2 gap-2 mt-2" data-testid="phone-visibility">
            {[["public", "Everyone"], ["friends", "Friends only"], ["selected", "Selected friends"], ["private", "Only me"]].map(([v, l]) => (
              <button key={v} type="button" data-testid={`phone-vis-${v}`} onClick={() => setPhoneVis(v)}
                className={`rounded-full py-2 text-sm font-medium border transition-colors ${phoneVis === v ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "bg-white/5 border-white/10 text-[#E8E4F0]"}`}>{l}</button>
            ))}
          </div>
          {phoneVis === "selected" && (
            <div className="mt-3 rounded-xl border border-white/10 bg-white/5 p-2 max-h-48 overflow-y-auto" data-testid="phone-friend-picker">
              {friends.length === 0 ? <p className="text-[#A19DB0] text-xs p-2">Add friends first to choose who sees your number.</p> :
                friends.map((f) => {
                  const on = phoneSel.includes(f.id);
                  return (
                    <button key={f.id} type="button" data-testid={`phone-friend-item-${f.id}`} onClick={() => setPhoneSel((s) => (on ? s.filter((x) => x !== f.id) : [...s, f.id]))}
                      className={`w-full flex items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors ${on ? "bg-[#D4AF37]/15 text-[#D4AF37]" : "text-[#E8E4F0] hover:bg-white/5"}`}>
                      <span>{f.name}</span>{on && <Check size={16} />}
                    </button>
                  );
                })}
            </div>
          )}
        </div>
        <div id="hl-gender" className={`mt-4 rounded-xl transition-shadow ${hl === "gender" ? "ring-2 ring-[#D4AF37] p-3 -mx-1" : ""}`}>
          <label className="text-xs text-[#A19DB0]">I am</label>
          <div className="flex gap-2 mt-2" data-testid="profile-gender">
            {[["M", "Male"], ["F", "Female"]].map(([v, l]) => (
              <button key={v} data-testid={`gender-${v}`} onClick={async () => { try { const r = await api.put("/profile", { gender: v }); setUser(r.data); toast.success("Saved"); } catch (e) { toast.error("Couldn't save"); } }}
                className={`flex-1 rounded-full py-2.5 text-sm font-semibold border transition-colors ${user.gender === v ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "bg-white/5 border-white/10 text-[#E8E4F0]"}`}>{l} ({v})</button>
            ))}
          </div>
          {!user.gender && <p className="text-[#E5A84B] text-xs mt-2">Required before you can buy any pass.</p>}
        </div>
        <button onClick={saveProfile} disabled={saving} data-testid="profile-save" className="w-full bg-[#D4AF37] text-black font-semibold rounded-full py-3 active:scale-[0.98] transition-transform disabled:opacity-60">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      <div className="mt-6 rounded-3xl bg-white/5 border border-white/10 divide-y divide-white/10">
        {links.map((l) => {
          const Icon = l.icon;
          return (
            <button key={l.to} onClick={() => navigate(l.to)} data-testid={l.tid} className="w-full flex items-center gap-4 px-5 py-4 active:bg-white/5 transition-colors">
              <Icon size={18} className="text-[#D4AF37]" />
              <span className="flex-1 text-left text-sm font-medium">{l.label}</span>
              <ChevronRight size={18} className="text-[#A19DB0]" />
            </button>
          );
        })}
      </div>

      <div className="mt-6 rounded-3xl bg-white/5 border border-white/10 p-5" data-testid="profile-attendance-history">
        <p className="font-display text-lg font-semibold flex items-center gap-2"><CalendarCheck size={18} className="text-[#D4AF37]" /> Attendance history</p>
        {attendance.length === 0 ? (
          <p className="text-[#A19DB0] text-sm mt-2">No check-ins yet — scan in at a class or social to see it here.</p>
        ) : (
          <div className="mt-3 space-y-2 max-h-80 overflow-y-auto pr-1">
            {attendance.map((it) => (
              <div key={it.id} data-testid={`attendance-row-${it.id}`} className="flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 p-3">
                <div className="w-9 h-9 rounded-full bg-[#9D4EDD]/20 flex items-center justify-center shrink-0"><CalendarCheck size={16} className="text-[#9D4EDD]" /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{it.title || (it.ref_type === "class" ? "Class check-in" : "Event check-in")}</p>
                  <p className="text-[#A19DB0] text-xs">{fmtDateTime(it.created_at)} · {it.ref_type}{it.credits_used > 0 ? ` · -${it.credits_used} pass` : ""}</p>
                </div>
                {isAdmin && (
                  <button onClick={() => delAttendance(it.id)} data-testid={`attendance-delete-${it.id}`} aria-label="Delete attendance record"
                    className="shrink-0 text-[#E57373] p-1.5 rounded-lg hover:bg-[#E57373]/10 active:scale-90 transition-transform"><Trash2 size={15} /></button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <button onClick={enablePush} disabled={pushOn || pushBusy} data-testid="enable-push" className="w-full mt-6 flex items-center justify-center gap-2 bg-white/5 border border-white/10 rounded-full py-3.5 font-semibold active:scale-[0.98] transition-transform disabled:opacity-60">
        <Bell size={18} className="text-[#D4AF37]" /> {pushOn ? "Notifications enabled" : pushBusy ? "Enabling…" : "Enable push notifications"}
      </button>

      {user.role === "super_admin" && <DanceOptInToggle />}

      <button onClick={logout} data-testid="logout-btn" className="w-full mt-3 flex items-center justify-center gap-2 bg-white/5 border border-white/10 rounded-full py-3.5 font-semibold active:scale-[0.98] transition-transform">
        <LogOut size={18} /> Log out
      </button>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <button data-testid="delete-account-btn" className="w-full mt-3 flex items-center justify-center gap-2 text-[#E57373] border border-[#E57373]/30 rounded-full py-3.5 font-semibold active:scale-[0.98] transition-transform">
            <Trash2 size={18} /> Delete account
          </button>
        </AlertDialogTrigger>
        <AlertDialogContent className="bg-[#140F1E] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete your account?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A19DB0]">This permanently removes your profile, passes, bookings and history. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} data-testid="confirm-delete" className="bg-[#E57373] text-black hover:bg-[#ef8a8a]">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <p className="text-center text-[#A19DB0]/50 text-xs mt-6">Darkchata Management System · v1.0</p>
    </Screen>
  );
}
